import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Divider,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { getPlayersData, getSearchValue } from '../../licenseAnalyticsSupport/getLicenseAnalyticsActions';
import { ClubNameCell } from '../../explorePageComponents/finance/academyTableComponent';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';
import './playersComponent.css';

const formatDate = (date:any) => {
  const d = new Date(date);

  if (isNaN(d.getTime())) return '-';

  const year = d.getFullYear().toString();
  const month = (d.getMonth() + 1).toString().padStart(2, '0'); // Months are zero-indexed
  const day = d
    .getDate()
    .toString()
    .padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const PlayersSubTable = React.memo(({ clubData, setSelectedRow }:any) => {
  const [t] = useTranslation();
  return (
    <TableContainer sx={{ maxHeight: 500, width: 'fit-content' }}>
      <Table stickyHeader aria-label="collapsible table">
        <TableRow className="sticky-header">
          <TableCell style={{ fontWeight: 'bold', padding: '5px 16px', minWidth: '100px', maxWidth: '375px' }}>{t('CLUB_NAME')}</TableCell>
          <TableCell style={{ fontWeight: 'bold', padding: '5px 16px', minWidth: '100px', maxWidth: '375px' }}>{t('DC_8_COUNTRY')}</TableCell>
          <TableCell style={{ fontWeight: 'bold', padding: '5px 16px', minWidth: '100px', maxWidth: '375px' }}>{t('Club')}</TableCell>
          <TableCell style={{ fontWeight: 'bold', padding: '5px 16px', minWidth: '100px', maxWidth: '375px' }}>{t('START_DATE')}</TableCell>
          <TableCell style={{ fontWeight: 'bold', padding: '5px 16px', minWidth: '100px', maxWidth: '375px' }}>{t('END_DATE')}</TableCell>
          <TableCell style={{ padding: '5px 16px' }}>
            <Tooltip title={'Close'}>
              <FontAwesomeIcon
                className="icon ml10"
                icon={faClose}
                style={{ cursor: 'pointer', fontSize: 18 }}
                onClick={() => setSelectedRow(null)}
              />
            </Tooltip>
          </TableCell>
        </TableRow>
        <TableBody>
          {clubData?.clubCareers?.length > 0 && (
            <TableRow>
              <TableCell style={{ fontWeight: 'bold', padding: '5px 16px' }}>{t('DC_8_CLUB_CAREER')}</TableCell>
              <TableCell style={{ padding: '5px 16px' }} colSpan={6} />
            </TableRow>
          )}
          {clubData?.clubCareers?.map((clubCareer:any, index:any) => {
            return (
              <TableRow key={index}>
                <TableCell style={{ fontSize: '0.75rem', padding: '2px 16px', minWidth: '100px', maxWidth: '375px' }}>
                  <ClubNameCell clubData={clubData} getImageUrl={(data:any) => data?.club?.imagePath} />
                </TableCell>
                <TableCell style={{ padding: '2px 16px', minWidth: '100px', maxWidth: '375px' }}>{clubCareer.country}</TableCell>
                <TableCell style={{ padding: '2px 16px', minWidth: '100px', maxWidth: '375px' }}>{clubCareer.club}</TableCell>
                <TableCell style={{ padding: '2px 16px', minWidth: '100px', maxWidth: '375px' }}>{formatDate(clubCareer.dateFrom)}</TableCell>
                <TableCell style={{ padding: '2px 16px', minWidth: '100px', maxWidth: '375px' }}>{formatDate(clubCareer.dateTo)}</TableCell>
                <TableCell style={{ padding: '2px 16px', minWidth: '100px', maxWidth: '375px' }}></TableCell>
              </TableRow>
            );
          })}
          {clubData?.internationalCareers?.length > 0 && (
            <TableRow>
              <TableCell style={{ fontWeight: 'bold' }}>{t('DC_8_INTERNATIONAL') + ' ' + t('PLAYER_CAREER')}</TableCell>
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell />
            </TableRow>
          )}
          {clubData?.internationalCareers?.map((internationalCareer:any, index:any) => {
            return (
              <TableRow key={index}>
                <TableCell style={{ fontSize: '0.75rem' }}>
                  <ClubNameCell clubData={clubData} getImageUrl={(data:any) => data?.club?.imagePath} />
                </TableCell>
                <TableCell>{internationalCareer.country}</TableCell>
                <TableCell>{internationalCareer.club}</TableCell>
                <TableCell>{formatDate(internationalCareer.dateFrom)}</TableCell>
                <TableCell>{formatDate(internationalCareer.dateTo)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
});
const Row = React.memo(({ index, selectedRow, setSelectedRow, player }:any) => {
  // Find the first current club where dateTo is null
  const currentClub = player?.clubCareers?.find((career:any) => career.dateTo == null)?.club || '';

  // Find the first international club where dateTo is null
  const internationalClub = player?.internationalCareers?.find((career:any) => career.dateTo == null)?.club || '';

  return (
    <TableRow
      hover
      selected={index === selectedRow ? true : false}
      onClick={() => {
        setSelectedRow(index);
      }}
    >
      <TableCell style={{ fontSize: '0.75rem', padding: '0px 16px', minWidth: '100px', maxWidth: '375px' }}>
        <ClubNameCell clubData={player} getImageUrl={(data:any) => data?.club?.imagePath} />
      </TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '0px 16px', minWidth: '100px', maxWidth: '375px' }}>{player?.name}</TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '0px 16px', minWidth: '100px', maxWidth: '375px' }}>{formatDate(player?.dateOfBirth)}</TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '0px 16px', minWidth: '100px', maxWidth: '375px' }}>{currentClub}</TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '0px 16px', minWidth: '100px', maxWidth: '375px' }}>{internationalClub}</TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '0px 16px', width: '640px' }}></TableCell>
    </TableRow>
  );
});

const PlayersExplorePage = React.memo(() => {
  const playersData = useSelector(getPlayersData);
  const searchValue = useSelector(getSearchValue);
  const [selectedRow, setSelectedRow] = useState();
  const [objectList, setObjectList] = useState(playersData);
  const [isAscending, setIsAscending] = useState(true);
  const [isAscending1, setIsAscending1] = useState(false);
  const [criteria, setCriteria] = useState('Club');
  const [playersCount, setPlayersCount] = useState();
  const [t] = useTranslation();

  // Memoized sorting function
  const sortedObjectList = useMemo(() => {
    return [...objectList].sort((a, b) => {
      if (criteria === 'Club') {
        const nameA = a.club?.name.toLowerCase();
        const nameB = b.club?.name.toLowerCase();
        return isAscending ? nameA?.localeCompare(nameB) : nameB?.localeCompare(nameA);
      } else {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        return isAscending1 ? nameA?.localeCompare(nameB) : nameB?.localeCompare(nameA);
      }
    });
  }, [objectList, isAscending, isAscending1]);

  // Callback for sorting to avoid recreation on every render
  const handleSortClick1 = useCallback(() => {
    setCriteria('Player');
    setIsAscending1(prev => !prev);
  }, []);

  // Callback for sorting to avoid recreation on every render
  const handleSortClick = useCallback(() => {
    setCriteria('Club');
    setIsAscending(prev => !prev);
  }, []);

  useEffect(() => {
    if (searchValue != '') {
      const filteredList = playersData.filter((item:any) => {
        // Find the first current club where dateTo is null
        const currentClub = item?.clubCareers?.find((career:any) => career.dateTo == null)?.club || '';

        // Find the first international club where dateTo is null
        const internationalClub = item?.internationalCareers?.find((career:any) => career.dateTo == null)?.club || '';

        // Perform the search check
        return (
          item.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
          item?.club?.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
          formatDate(item?.dateOfBirth)
            ?.toLowerCase()
            .includes(searchValue.toLowerCase()) ||
          currentClub?.toLowerCase().includes(searchValue.toLowerCase()) ||
          internationalClub?.toLowerCase().includes(searchValue.toLowerCase())
        );
      });
      setObjectList(filteredList);
      setPlayersCount(filteredList?.length);
    } else {
      setObjectList(playersData);
      setPlayersCount(playersData?.length);
    }
  }, [searchValue, playersData]);

  const rowsSkeleton = Array.from({ length: 10 }).map((_, index) => (
    <TableRow key={index}>
      {Array.from({ length: 5 }).map((_, cellIndex) => (
        <TableCell key={cellIndex}>
          <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="100%" height={20} />
        </TableCell>
      ))}
    </TableRow>
  ));

  return (
    <>
      <Typography sx={{ width: '33%', flexShrink: 0, fontWeight: 'bold', fontFamily: 'Montserrat', padding: '6px' }}>
        {t('PLAYERS_DETAIL') + ` (${playersCount} ${t('ROWS')})`}
      </Typography>
      <Divider />
      <TableContainer className={'table-container'} sx={{ maxHeight: '600px' }}>
        <Table stickyHeader aria-label="collapsible table" sx={{ width: 'max-content' }}>
          <TableRow className="sticky-header">
            <TableCell style={{ fontWeight: 'bold', padding: '6px 16px' }}>
              {t('DC_5_CLUB')}
              {isAscending ? (
                <ArrowDropUpIcon onClick={handleSortClick} />
              ) : (
                <ArrowDropDownIcon onClick={handleSortClick} />
              )}
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', padding: '6px 16px' }}>
              {t('NAME')}
              {isAscending ? (
                <ArrowDropUpIcon onClick={handleSortClick1} />
              ) : (
                <ArrowDropDownIcon onClick={handleSortClick1} />
              )}
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', padding: '6px 16px' }}>{t('DC_5_DOB')}</TableCell>
            <TableCell style={{ fontWeight: 'bold', padding: '6px 16px' }}>
              {t('DC_8_CURRENT_CLUB')}
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', padding: '6px 16px' }}>{t('DC_8_INTERNATIONAL')}</TableCell>
            <TableCell style={{ width: '640px' }}></TableCell>
          </TableRow>
          <TableBody>
            {playersData && playersData.length > 0 ? (
              sortedObjectList.length > 0 ? (
                sortedObjectList?.map((player, index) => {
                  return (
                    <Row
                      key={index}
                      index={index}
                      selectedRow={selectedRow}
                      setSelectedRow={setSelectedRow}
                      player={player}
                    />
                  );
                })
              ) : searchValue === '' ? (
                rowsSkeleton
              ) : (
                <TableRow>
                  <TableCell colSpan={6} style={{ textAlign: 'center' }}>
                    {t('No_Matching_Records_Found')}...
                  </TableCell>
                </TableRow>
              )
            ) : playersData?.length == 0 ? (
              <TableRow>
                <TableCell colSpan={6} style={{ textAlign: 'center' }}>
                  {t('No_Matching_Records_Found')}...
                </TableCell>
              </TableRow>
            ) : (
              rowsSkeleton
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {selectedRow != null && objectList?.[selectedRow] && (
        <div style={{ paddingTop: '20px' }}>
          <PlayersSubTable clubData={objectList?.[selectedRow]} setSelectedRow={setSelectedRow} />
        </div>
      )}
    </>
  );
});

export default PlayersExplorePage;
